// ============================================================================
// componentes/Rca.tsx — el segmento de análisis de causa raíz
// ----------------------------------------------------------------------------
// Vive FUERA de la vista de una línea, y no es un capricho de navegación: un
// análisis puede abarcar varias líneas, o ninguna todavía. Dentro de una línea,
// el patrón más caro de un parque —el mismo componente fallando en apoyos de
// líneas distintas— sería invisible por construcción.
//
// LO QUE ESTA PANTALLA HACE DISTINTO, y es todo el punto:
//
//   · El objeto central NO es la causa: es la TABLA DE DESCARTES. TODAS las
//     familias de causas se pintan siempre, en el mismo orden, aunque
//     nadie las haya mirado. Una espina que desaparece al faltar el dato se lee
//     como «eso ya no aplica».
//   · Hay un bloque fijo con LO QUE ESTE ANÁLISIS NO PUEDE AFIRMAR. Un informe
//     que omite sus límites parece más fuerte y es más frágil.
//   · **NO HAY BOTÓN DE CERRAR** mientras falte alguna condición. En su sitio va
//     la lista de lo que falta. Un botón deshabilitado invita a buscar cómo
//     habilitarlo; una lista de lo que falta invita a ir a buscarlo.
//   · No hay ranking de hipótesis, ni causa sugerida, ni porcentaje de avance.
//     Ordenar es dictaminar, y un análisis «al 92 %» empuja a cerrar.
//
// Todo el juicio lo hace `nucleo/rca.js`, que es puro y está probado. Aquí se
// pinta; no se decide.
// ============================================================================
import { useState } from 'react';
import {
  evaluarEspinas, condicionesCausaRaiz, auditarRespaldo, candidatosCausaRaiz,
  causasDeclaradas, avisoCausas, ESPINAS, ROTULO_ESPINA,
} from '@lineas/nucleo/rca';
import type { AccionCapa, AnalisisCausa, Evidencia, SondeoClima } from '@lineas/contratos';
import { descargar, selloFecha } from '../exportar/descargar';

/**
 * Genera y descarga el informe del análisis.
 *
 * El generador va por importación diferida: es papel imprimible que casi nunca
 * se pide, y no tiene por qué viajar en el paquete que se descarga al abrir la
 * aplicación.
 */
async function generarInforme(a: AnalisisCausa, acciones: AccionCapa[], sondeos: SondeoClima[]): Promise<void> {
  const { informeRcaHtml } = await import('@lineas/exportar/informeRca');
  const html = informeRcaHtml({
    analisis: a, acciones, sondeos,
    meta: { generadoEn: new Date().toLocaleString('es-CO') },
  });
  descargar(`${a.codigo ?? 'analisis'}_${selloFecha()}.html`, 'text/html', html);
}
import { almacen, useRca, useQuien } from '../datos/enlace';
import { puede } from '../datos/permisos';
import { EditorPorques, EditorArbol, EditorHipotesis, EditorAusencias, EditorAcciones, ClimaEvento } from './RcaEditores';
import { nf } from '../vistas/formato';

/** El rótulo de una familia. El mapa lo publica el núcleo; aquí solo se indexa con tipo. */
const rotuloEspina = (e: string): string => (ROTULO_ESPINA as Record<string, string>)[e] ?? e;

// El rótulo de cada familia lo publica `nucleo/rca.js`: una lista, un dueño.

/**
 * Una causa raíz ya declarada, tal como la devuelve `causasDeclaradas()` del
 * núcleo — que es JavaScript, así que el tipo se declara aquí. `esLegado` marca
 * las que vienen del campo viejo de una sola causa.
 */
type CausaDeclarada = {
  nodoId?: string;
  enunciado?: string;
  tipo?: string;
  declaradaEn?: string;
  declaradaPor?: string;
  condicionesNoCumplidas?: string[];
  esLegado?: boolean;
};

/** La tipología de IEC 62740:2015 cl. 4. Manda sobre qué se puede prometer al corregir. */
const TIPO_CAUSA: Record<string, string> = {
  unica: 'Única',
  multiple: 'Múltiple — corregir cualquiera evita el evento',
  contribuyente: 'Contribuyente — cambia la probabilidad, puede no evitarlo',
};

// ── El índice del segmento ──────────────────────────────────────────────────

function Indice({ analisis, avisoRuta }: { analisis: AnalisisCausa[]; avisoRuta?: string }) {
  if (!analisis.length) {
    return (
      <section className="panel vacio">
        <div className="vacio-t">Todavía no hay ningún análisis de causa raíz</div>
        <p className="vacio-c">
          Este segmento vive <b>fuera</b> de las líneas a propósito: un análisis puede abarcar
          varias, o ninguna todavía. La causa raíz más cara de un parque es la que se repite — el
          mismo componente fallando en apoyos de líneas distintas es <b>una</b> causa raíz y
          <b> varios</b> eventos, y desde dentro de una línea ese patrón no se ve.
        </p>
        <p className="fine">
          El primero se abre desde un evento de falla. Esa conexión es lo siguiente que se
          construye; hasta entonces la lista está vacía, y estar vacía es su estado correcto —
          no se rellena con ejemplos.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Análisis de causa raíz · {nf(analisis.length)}</h2>
      {/* El enlace pedía un expediente concreto y no está. No se abre «uno
          parecido»: se dice qué pasó y se deja elegir. */}
      {avisoRuta && <p className="alerta" role="status">{avisoRuta}</p>}
      <div className="tabla-caja">
        <table className="tabla">
          <thead>
            <tr><th>Código</th><th>Título</th><th>Estado</th><th>Alcance</th><th className="num">Abierto</th></tr>
          </thead>
          <tbody>
            {analisis.map((a) => {
              const n = a.alcance.lineaIds.length + a.alcance.apoyoIds.length + a.alcance.investigacionIds.length;
              return (
                <tr key={a.id} onClick={() => void almacen.verAnalisis(a.id)} style={{ cursor: 'pointer' }}>
                  <td className="destaca">{a.codigo}</td>
                  <td>{a.titulo}</td>
                  <td>{a.estado === 'cerrado'
                    ? <span className="pill ok">cerrado</span>
                    : a.estado === 'sin_conclusion'
                      ? <span className="pill av">sin conclusión</span>
                      : <span className="pill inf">{a.estado}</span>}</td>
                  <td>{n ? `${n} elemento(s)` : <span className="h-pill">sin activo identificado</span>}</td>
                  <td className="num">{a.abiertoEn?.slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── Un análisis abierto ─────────────────────────────────────────────────────

function Abierto({ a, evidencias, acciones, sondeos, fallo, noSePudoLeer }: { a: AnalisisCausa; evidencias: Evidencia[]; acciones: AccionCapa[]; sondeos: SondeoClima[]; fallo?: { mensaje: string; queSeIntentaba: string }; noSePudoLeer?: { evidencias?: string; acciones?: string; sondeos?: string } }) {
  const quien = useQuien();
  const editable = puede(quien, 'expedientes.editar');
  const cond = condicionesCausaRaiz(a);
  const respaldo = auditarRespaldo(a);
  const faltan = cond.condiciones.filter((c) => !c.cumple);
  // Quién decide qué causas hay es el núcleo, no esta pantalla: si aquí se
  // eligiera entre el campo viejo y el nuevo, pantalla e informe acabarían
  // enseñando causas distintas del mismo expediente.
  const declaradas: CausaDeclarada[] = causasDeclaradas(a);

  return (
    <>
      <div className="rca-cab">
        <button type="button" className="boton chico" onClick={() => almacen.volverAlIndice()}>← Todos los análisis</button>
        {/* El informe se puede generar SIEMPRE, esté el análisis a medias o
            cerrado: durante semanas el estado normal es no tener causa raíz, y
            un avance que no se puede enseñar no sirve para trabajar en equipo.
            El propio documento dice en su portada si es AVANCE o CONCLUSIÓN. */}
        {/* ⚠️ AQUÍ se hace cumplir `informes.generar`. Hasta el 2026-09-06 esa
            función del catálogo no la exigía NADIE —ni una regla, ni un
            trabajador, ni esta pantalla—, o sea que era decorativa: justo lo que
            el propio catálogo prohíbe («no existe aquí ningún campo que no se
            haga cumplir en alguna parte», `99 §ADR-100`). El informe no escribe
            nada, así que el espectador lo conserva; quien no la traiga, no. */}
        {puede(quien, 'informes.generar') && (
          <button type="button" className="boton chico" onClick={() => void generarInforme(a, acciones, sondeos)}>
            Informe del análisis
          </button>
        )}
        <h2 className="linea-titulo">{a.codigo} — {a.titulo}</h2>
      </div>

      {/* Un fallo al GUARDAR no tira la pantalla: lo tecleado sigue delante y
          esta franja dice que NO se guardó. Antes esto ponía el expediente en
          estado de error y desmontaba el editor entero, y el texto se perdía. */}
      {fallo && (
        <section className="rca-fallo-guardar" role="alert">
          <b>No se guardó.</b> Falló al {fallo.queSeIntentaba}: {fallo.mensaje}
          <p>
            <b>Lo que escribiste sigue en pantalla y no se ha perdido.</b> Vuelve a pulsar
            «Guardar» cuando tengas conexión. Si el fallo se repite, copia el texto antes de
            recargar: recargar sí lo borra.
          </p>
        </section>
      )}

      <TablaDescartes a={a} evidencias={evidencias} noSePudoLeer={noSePudoLeer?.evidencias} />

      {/* Se dice UNA vez y arriba, no en cada editor: quien lee tiene que saber
          desde el principio por qué no ve mandos, en vez de descubrirlo al
          buscar el botón de guardar. Lo que se LEE no cambia. */}
      {!editable && (
        <p className="aviso" role="status">
          <b>Solo lectura.</b> Este expediente se enseña completo, pero su sesión no puede
          modificarlo: le falta la función «expedientes.editar».
        </p>
      )}
      <EditorPorques a={a} evidencias={evidencias} />
      <EditorArbol a={a} evidencias={evidencias} />
      <EditorHipotesis a={a} evidencias={evidencias} />
      <ClimaEvento sondeos={sondeos} />
      <EditorAusencias a={a} />
      {/* Las acciones van DESPUÉS de las ausencias y ANTES de declarar la causa:
          se pueden proponer sin causa raíz declarada —en mantenimiento real las
          correctivas urgentes se toman el mismo día— pero leerlas después de lo
          que el análisis NO puede afirmar evita prometer sobre un hueco. */}
      <EditorAcciones acciones={acciones} arbol={a.arbol} />

      {/* El respaldo, en frío: cuántas afirmaciones no se apoyan en nada. */}
      <section className="panel">
        <h2>Respaldo del análisis</h2>
        {respaldo.aviso && <p className="alerta">{respaldo.aviso}</p>}
        <p className="fine">
          {nf(respaldo.sinRespaldo)} de {nf(respaldo.totalAfirmaciones)} afirmaciones no tienen
          evidencia enlazada. {a.limitaciones.map((l) => l).join(' · ')}
        </p>
      </section>

      {/* EL CIERRE — sin botón mientras falte algo */}
      <section className="panel">
        <h2>Causa raíz</h2>
        {declaradas.length > 0 ? (
          <>
            {declaradas.length > 1 && (
              <p className="fine">
                <b>{declaradas.length} causas raíz declaradas.</b> No son candidatas: son las que se
                firmaron. Corregir una sola deja las demás abiertas.
              </p>
            )}
            {declaradas.map((c, i) => (
              <div key={c.nodoId ?? i}>
                <p className="ok"><b>{c.enunciado}</b></p>
                <p className="fine">
                  {(c.tipo && TIPO_CAUSA[c.tipo]) ?? c.tipo} · declarada por una persona el {c.declaradaEn?.slice(0, 10)}.
                </p>
                {(c.condicionesNoCumplidas?.length ?? 0) > 0 && (
                  <p className="alerta">
                    Se declaró con {c.condicionesNoCumplidas?.length} condición(es) sin cumplir.
                    Queda constancia: {c.condicionesNoCumplidas?.join(' · ')}
                  </p>
                )}
              </div>
            ))}
            {avisoCausas(declaradas) && <p className="alerta">{avisoCausas(declaradas)}</p>}
            {declaradas.some((c) => c.esLegado) && (
              <p className="fine">
                Este expediente se firmó cuando solo cabía UNA causa raíz. Que aparezca una sola no
                significa que se descartaran otras: significa que no se podían escribir.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="fine">
              Todavía no hay causa raíz declarada, y durante días ése es el estado normal. El
              sistema <b>no la propone</b>: calcula qué condiciones se cumplen y quién firma decide.
            </p>
            <ul className="calidad-lista">
              {cond.condiciones.map((c) => (
                <li key={c.clave} className={c.cumple ? 'calidad-item info' : 'calidad-item atencion'}>
                  {c.cumple ? '✓ ' : '✗ '}{c.texto}
                  {c.detalle && <><br /><i className="fine">{c.detalle}</i></>}
                </li>
              ))}
            </ul>
            {/* El botón NO existe mientras falte una condición. En su sitio, la
                lista de arriba. Un botón deshabilitado invita a buscar cómo
                habilitarlo; una lista de lo que falta invita a ir a buscarlo. */}
            {faltan.length === 0 ? <Declarar a={a} /> : (
              <p className="fine">
                Faltan {faltan.length} de {cond.total} condiciones para poder declarar una causa raíz.
              </p>
            )}
          </>
        )}
      </section>
    </>
  );
}


/**
 * LA TABLA DE DESCARTES, EDITABLE.
 *
 * Es el corazón del método y por eso se rellena aquí, no en un formulario
 * aparte: el defecto aparece EN EL MOMENTO en que se comete. Si alguien marca
 * «descartada» sin evidencia, la fila lo dice en el acto — no al guardar, ni en
 * un resumen al final que nadie lee.
 *
 * Las once salen siempre, incluso las que nadie ha tocado.
 */
function TablaDescartes({ a, evidencias, noSePudoLeer }: { a: AnalisisCausa; evidencias: Evidencia[]; noSePudoLeer?: string }) {
  const editable = puede(useQuien(), 'expedientes.editar');
  const [borrador, setBorrador] = useState(() => {
    // Se indexa por TEXTO a propósito: mientras se edita, «estado» puede estar
    // vacío —«sin mirar»—, que no es un valor del contrato. Solo lo que tiene
    // estado se manda a validar.
    const m = new Map<string, (typeof a.espinas)[number]>(a.espinas.map((e) => [e.espina, e]));
    return evaluarEspinas(a.espinas).map((e) => ({
      espina: e.espina,
      estado: m.get(e.espina)?.estado ?? '',
      motivo: m.get(e.espina)?.motivo ?? '',
      datoQueFalta: m.get(e.espina)?.datoQueFalta ?? '',
      evidenciaIds: m.get(e.espina)?.evidenciaIds ?? [],
    }));
  });
  const [guardando, setGuardando] = useState(false);

  const tocar = (espina: string, campo: string, valor: string) =>
    setBorrador((b) => b.map((x) => (x.espina === espina ? { ...x, [campo]: valor } : x)));

  /** Enlazar y desenlazar es simétrico: una evidencia mal puesta se quita igual de fácil. */
  const alternarEvidencia = (espina: string, evId: string) =>
    setBorrador((b) => b.map((x) => (x.espina !== espina ? x : {
      ...x,
      evidenciaIds: x.evidenciaIds.includes(evId)
        ? x.evidenciaIds.filter((i) => i !== evId)
        : [...x.evidenciaIds, evId],
    })));

  // El juicio lo hace el motor, en vivo. La pantalla no decide nada.
  const conEstado = borrador.filter((x) => x.estado);
  const juicio = new Map<string, { defectos: string[] }>(
    evaluarEspinas(conEstado.map((x) => ({ ...x, datoQueFalta: x.datoQueFalta || undefined })))
      .map((e: { espina: string; defectos: string[] }) => [e.espina, e]),
  );

  const guardar = async () => {
    setGuardando(true);
    try {
      await almacen.guardarParte({ espinas: conEstado.map((x) => ({
        espina: x.espina, estado: x.estado, motivo: x.motivo,
        evidenciaIds: x.evidenciaIds,
        ...(x.datoQueFalta ? { datoQueFalta: x.datoQueFalta } : {}),
      })) });
    } finally { setGuardando(false); }
  };

  const sinMotivo = conEstado.filter((x) => !x.motivo.trim()).length;

  return (
    <section className="panel">
      <h2>Las {ESPINAS.length} familias de causas</h2>
      <p className="fine">
        Se recorren <b>todas</b>, siempre. Descartar o sostener una familia exige evidencia
        enlazada; decir «no evaluable» exige nombrar el dato que falta. No existe el estado
        «no aplica»: es el atajo que vacía un Ishikawa sin descartar nada.
      </p>
      {/* ⚠️ TRES ESTADOS, NO DOS. «No hay» y «no se pudo mirar» se leen igual en
          una pantalla y son cosas opuestas: con la primera se descarta una
          familia con la conciencia tranquila (`32 · L-44`). */}
      {evidencias.length === 0 && noSePudoLeer && (
        <p className="aviso">
          <b>No se pudieron leer las evidencias de este expediente.</b> Eso <b>no</b> significa que
          no haya: significa que no se pudo comprobar. <b>No descarte ni sostenga ninguna familia
          apoyándose en esta pantalla</b> hasta que carguen — lo que decida aquí quedaría escrito
          como si nadie hubiera mirado.
          <span className="fine"> Motivo técnico: {noSePudoLeer}</span>
        </p>
      )}
      {evidencias.length === 0 && !noSePudoLeer && (
        <p className="aviso">
          <b>Este análisis no tiene ninguna evidencia disponible</b> para enlazar. Mientras siga
          así, marcar «descartada» o «sostenida» dejará el aviso de «sin evidencia enlazada» — y
          ese aviso es correcto: la afirmación no está respaldada.
        </p>
      )}

      <div className="tabla-caja">
        <table className="tabla">
          <thead>
            <tr><th style={{ width: '20%' }}>Familia</th><th style={{ width: '15%' }}>Estado</th><th>Motivo — obligatorio</th></tr>
          </thead>
          <tbody>
            {borrador.map((x) => {
              const j = juicio.get(x.espina);
              return (
                <tr key={x.espina}>
                  <td className="destaca">{rotuloEspina(x.espina)}</td>
                  <td>
                    <select className="rca-select" value={x.estado}
                      onChange={(e) => tocar(x.espina, 'estado', e.target.value)}>
                      <option value="">— sin mirar —</option>
                      <option value="abierta">abierta</option>
                      <option value="sostenida">sostenida</option>
                      <option value="descartada">descartada</option>
                      <option value="no_evaluable">no evaluable</option>
                    </select>
                  </td>
                  <td>
                    <textarea className="rca-motivo" rows={2} value={x.motivo}
                      placeholder={x.estado ? 'Por qué está en ese estado' : ''}
                      disabled={!x.estado}
                      onChange={(e) => tocar(x.espina, 'motivo', e.target.value)} />
                    {x.estado === 'no_evaluable' && (
                      <input className="rca-motivo" value={x.datoQueFalta}
                        placeholder="Qué dato falta, y quién lo tiene"
                        onChange={(e) => tocar(x.espina, 'datoQueFalta', e.target.value)} />
                    )}
                    {x.estado && evidencias.length > 0 && (
                      <div className="rca-evidencias">
                        <span className="rca-ev-rotulo">Evidencia que lo sostiene</span>
                        {evidencias.map((ev) => (
                          <label key={ev.id} className="rca-ev">
                            <input type="checkbox" checked={x.evidenciaIds.includes(ev.id)}
                              onChange={() => alternarEvidencia(x.espina, ev.id)} />
                            <span>{ev.pie ?? ev.componenteEsperado ?? ev.rutaObjeto.split('/').pop()}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {j?.defectos.map((d: string) => <div key={d} className="rca-defecto">⚠ {d}</div>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rca-guardar">
        {editable && (
          <button type="button" className="boton chico" onClick={() => void guardar()}
            disabled={guardando || sinMotivo > 0}>
            {guardando ? 'Guardando…' : `Guardar ${conEstado.length} de ${ESPINAS.length}`}
          </button>
        )}
        {sinMotivo > 0 && (
          <span className="fine">
            {sinMotivo} familia(s) con estado y sin motivo. Un estado que no se puede auditar no vale.
          </span>
        )}
      </div>
    </section>
  );
}


/**
 * Declarar la causa raíz. Solo aparece cuando las seis condiciones se cumplen —
 * y aun así, quien declara es la persona: el motor calculó que SE PUEDE, no
 * QUÉ es.
 *
 * Se guarda además qué condiciones NO se cumplían en ese momento. Hoy son cero
 * por construcción, pero el campo existe para el día en que alguien decida
 * declarar con una pendiente: que quede constancia en el propio expediente y no
 * en la memoria de nadie.
 */
function Declarar({ a }: { a: AnalisisCausa }) {
  const editable = puede(useQuien(), 'expedientes.editar');
  const [nodoId, setNodoId] = useState('');
  const [enunciado, setEnunciado] = useState('');
  const [tipo, setTipo] = useState<'unica' | 'multiple' | 'contribuyente'>('unica');
  const [g, setG] = useState(false);

  const declarar = async () => {
    setG(true);
    try {
      // Se escribe SOLO la lista: mandar los dos campos a la vez lo rechaza el
      // molde de los datos a propósito, porque dejaría el documento diciendo dos
      // cosas del mismo hecho (la trampa `estado`/`cerrado`).
      // ⚠️ EL FORMULARIO SOLO SE VACÍA SI LA BASE CONFIRMÓ. Antes se vaciaba
      // siempre: `guardarParte` se traga el fallo —lo convierte en la franja
      // roja— así que este `await` terminaba bien incluso cuando no se había
      // escrito nada, y el enunciado de la causa raíz recién redactado
      // desaparecía del formulario. Y la franja decía, en negrita, «Lo que
      // escribiste sigue en pantalla y no se ha perdido»: mentía, en el acto más
      // caro de todo el expediente y al final de una pantalla muy larga, donde
      // el aviso ni siquiera se ve (`32 · L-67`).
      const guardado = await almacen.guardarParte({
        causasRaiz: [
          ...(a.causasRaiz ?? []),
          {
            nodoId, enunciado, tipo,
            declaradaPor: a.creadoPor,
            declaradaEn: new Date().toISOString(),
            condicionesNoCumplidas: [],
          },
        ],
        estado: 'en_revision',
      });
      if (guardado) { setNodoId(''); setEnunciado(''); }
    } finally { setG(false); }
  };

  // Qué nodos pueden sostener una causa raíz. Lo decide el núcleo, no esta
  // pantalla: aquí solo se pinta la marca y el motivo.
  const { candidatos, declarables, aviso } = candidatosCausaRaiz(a.arbol);
  // Cinturón: si el nodo elegido dejara de calificar —el árbol se edita en otra
  // pestaña—, el botón no puede seguir vivo con la selección vieja dentro.
  const elegido = candidatos.find((c) => c.id === nodoId);
  const eligeValido = Boolean(elegido?.puedeSerCausaRaiz);

  return (
    <div className="rca-declarar">
      <p className="ok">
        Se cumplen las seis condiciones. <b>Declarar la causa raíz sigue siendo un acto tuyo</b>:
        el motor calculó que se puede, no cuál es.
      </p>

      {aviso ? <p className="alerta">{aviso}</p> : (
        <select className="rca-select" value={nodoId} onChange={(e) => setNodoId(e.target.value)}>
          <option value="">— qué nodo del árbol es la causa raíz —</option>
          {candidatos.map((c) => (
            // Los que no califican se pintan igual, pero no se pueden elegir. Un
            // nodo que desaparece se lee como que se perdió el trabajo; uno que
            // dice por qué no puede serlo manda a seguir preguntando.
            <option key={c.id} value={c.id} disabled={!c.puedeSerCausaRaiz}>
              {c.enunciado.slice(0, 70)}
              {c.puedeSerCausaRaiz
                ? (c.sinEvidencia ? '  ⚠️ sin evidencia enlazada' : '')
                : `  — no puede serlo: ${c.motivo}`}
            </option>
          ))}
        </select>
      )}

      {elegido?.sinEvidencia && eligeValido && (
        <p className="alerta">
          Ese nodo no tiene ninguna evidencia enlazada. Puedes declararlo igual —quien firma eres
          tú—, pero el informe lo va a decir con esas palabras.
        </p>
      )}

      {/* El TIPO no es burocracia: manda sobre qué se puede prometer. Corregir
          una contribuyente cambia la probabilidad de que vuelva, pero puede no
          evitarlo — prometer lo contrario es el error que esto impide. */}
      <select className="rca-select" value={tipo}
        onChange={(e) => setTipo(e.target.value as 'unica' | 'multiple' | 'contribuyente')}>
        {Object.entries(TIPO_CAUSA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <textarea className="rca-motivo" rows={3} value={enunciado}
        placeholder="La causa raíz, con tus palabras. Es lo que se firma."
        onChange={(e) => setEnunciado(e.target.value)} />
      {editable && (
        <button type="button" className="boton chico"
          disabled={g || !eligeValido || !enunciado.trim()} onClick={() => void declarar()}>
          {g ? 'Declarando…' : (a.causasRaiz ?? []).length ? 'Añadir otra causa raíz' : 'Declarar la causa raíz'}
        </button>
      )}
      {declarables > 0 && (
        <p className="fine">
          {declarables} de {candidatos.length} nodos del árbol pueden sostener una causa raíz. Los
          demás se quedan en el efecto, el modo de falla o el mecanismo físico.
        </p>
      )}
    </div>
  );
}

// ── El segmento ─────────────────────────────────────────────────────────────

export function Rca() {
  const r = useRca();
  if (r.fase === 'cerrado') return null;

  return (
    <div className="rca">
      <div className="rca-barra">
        <span className="col-rotulo">Análisis de causa raíz</span>
        <button type="button" className="boton chico" onClick={() => almacen.cerrarRca()}>
          Volver al parque
        </button>
      </div>
      {r.fase === 'cargando' && <section className="panel vacio"><div className="vacio-t">Cargando…</div></section>}
      {r.fase === 'error' && <section className="panel vacio"><div className="vacio-t">No se pudieron leer los análisis</div><p className="vacio-c">{r.mensaje}</p></section>}
      {r.fase === 'indice' && <Indice analisis={r.analisis} avisoRuta={r.avisoRuta} />}
      {r.fase === 'abierto' && <Abierto a={r.analisis} evidencias={r.evidencias} acciones={r.acciones} sondeos={r.sondeos} fallo={r.falloAlGuardar} noSePudoLeer={r.noSePudoLeer} />}
    </div>
  );
}
